import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Shield,
  AlertTriangle,
  CheckCircle,
  TrendingDown,
  TrendingUp,
  Download,
  XCircle,
  Info,
} from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

export const ComplianceDashboard: React.FC = () => {
  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ['compliance-dashboard'],
    queryFn: async () => {
      try {
        const response = await api.compliance.getDashboard();
        return response.data;
      } catch (err: any) {
        console.error('Compliance dashboard error:', err);
        console.error('Error response:', err.response?.data);
        throw err;
      }
    },
    refetchInterval: 60000, // Refresh every minute
  });

  const handleExportReport = async () => {
    try {
      toast.info('Generating compliance report...');
      const response = await api.compliance.getReport();
      
      // Create a downloadable JSON file
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `compliance-report-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Compliance report downloaded');
    } catch (err: any) {
      console.error('Failed to export report:', err);
      toast.error('Failed to export compliance report');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'good':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'good':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'critical':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Info className="h-5 w-5 text-gray-600" />;
    }
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(2)}%`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    const errorMessage = error instanceof Error 
      ? error.message 
      : (error as any)?.response?.data?.detail || 'Unknown error';
    
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          <div>
            <p className="font-semibold mb-1">Failed to load compliance dashboard</p>
            <p className="text-sm">{errorMessage}</p>
            <p className="text-xs mt-2 text-muted-foreground">
              Please check the browser console for more details or try refreshing the page.
            </p>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  const metrics30 = dashboardData?.metrics_30_days;
  const metrics7 = dashboardData?.metrics_7_days;
  
  // Check if dashboard is empty (no SMS activity ever, not just in last 30 days)
  // Use has_sms_campaigns (all-time) to determine if user has ever run SMS campaigns
  // If they have run campaigns but have no recent activity, still show the dashboard
  const hasSmsActivity = dashboardData?.has_sms_campaigns || metrics30?.total_messages_sent > 0 || dashboardData?.recent_opt_outs?.length > 0;
  const isEmpty = !hasSmsActivity;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-600" />
            Compliance Dashboard
          </h2>
          <p className="text-muted-foreground mt-1">
            Monitor your SMS/TCPA compliance metrics and status
          </p>
        </div>
        <Button onClick={handleExportReport} variant="outline" disabled={isEmpty}>
          <Download className="h-4 w-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Empty State */}
      {isEmpty && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <Shield className="h-16 w-16 text-blue-200" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-gray-900">Getting Started with Compliance</h3>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Your compliance dashboard is empty because you haven't sent any SMS campaigns yet. 
                  Start sending campaigns and this dashboard will automatically track your compliance metrics.
                </p>
              </div>
              <div className="pt-4">
                <Alert className="max-w-2xl mx-auto">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-left">
                    <strong>What gets tracked:</strong>
                    <ul className="mt-2 space-y-1 text-sm">
                      <li>• SMS consent rates and compliance status</li>
                      <li>• Opt-out requests and rates</li>
                      <li>• Message delivery metrics</li>
                      <li>• TCPA compliance warnings</li>
                      <li>• Suppression list management</li>
                    </ul>
                  </AlertDescription>
                </Alert>
              </div>
              <div className="pt-2">
                <Button 
                  onClick={() => window.location.href = '/campaigns'}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Create Your First Campaign
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Show normal dashboard if there's data */}
      {!isEmpty && (
        <>

      {/* Overall Status */}
      <Card>
        <CardHeader>
          <CardTitle>Overall Compliance Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {getStatusIcon(metrics30?.compliance_status)}
            <div className="flex-1">
              <Badge className={getStatusColor(metrics30?.compliance_status) + ' text-lg px-4 py-1'}>
                {metrics30?.compliance_status?.toUpperCase() || 'UNKNOWN'}
              </Badge>
              <p className="text-sm text-muted-foreground mt-2">
                Based on 30-day metrics (last updated: {format(new Date(), 'PPpp')})
              </p>
            </div>
          </div>

          {metrics30?.warnings && metrics30.warnings.length > 0 && (
            <div className="mt-4 space-y-2">
              {metrics30.warnings.map((warning: string, idx: number) => (
                <Alert key={idx} variant={warning.includes('CRITICAL') ? 'destructive' : 'default'}>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{warning}</AlertDescription>
                </Alert>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Opt-out Rate */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Opt-out Rate (30d)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPercentage(metrics30?.opt_out_rate || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Target: &lt; 1% | {metrics30?.opt_outs || 0} opt-outs
            </p>
            <div className="flex items-center gap-1 mt-2">
              {metrics30?.opt_out_rate > metrics7?.opt_out_rate ? (
                <>
                  <TrendingDown className="h-4 w-4 text-green-600" />
                  <span className="text-xs text-green-600">Improving</span>
                </>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4 text-red-600" />
                  <span className="text-xs text-red-600">Increasing</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Spam Complaint Rate */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Spam Complaints (30d)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPercentage(metrics30?.spam_complaint_rate || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Target: &lt; 0.1% | {metrics30?.spam_complaints || 0} complaints
            </p>
          </CardContent>
        </Card>

        {/* Delivery Rate */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Delivery Rate (30d)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPercentage(metrics30?.delivery_rate || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Target: &gt; 95% | {metrics30?.messages_delivered || 0} delivered
            </p>
          </CardContent>
        </Card>

        {/* Consent Rate */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Contacts with Consent</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPercentage(metrics30?.consent_rate || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics30?.contacts_with_consent || 0} of {metrics30?.total_contacts || 0} contacts
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Opt-outs */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Opt-outs (Last 7 Days)</CardTitle>
        </CardHeader>
        <CardContent>
          {dashboardData?.recent_opt_outs && dashboardData.recent_opt_outs.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboardData.recent_opt_outs.map((optOut: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{optOut.phone || optOut.email || 'Unknown'}</TableCell>
                    <TableCell>{format(new Date(optOut.date), 'PPp')}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{optOut.reason}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No recent opt-outs in the last 7 days
            </p>
          )}
        </CardContent>
      </Card>

      {/* Attestation Status */}
      <Card>
        <CardHeader>
          <CardTitle>Compliance Attestation</CardTitle>
        </CardHeader>
        <CardContent>
          {dashboardData?.attestation?.has_attested ? (
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-1" />
              <div>
                <p className="font-medium text-green-900">Attestation Complete</p>
                <p className="text-sm text-muted-foreground">
                  Last attested: {format(new Date(dashboardData.attestation.last_attested), 'PPp')}
                </p>
                <p className="text-sm text-muted-foreground">
                  Type: {dashboardData.attestation.attestation_type}
                </p>
              </div>
            </div>
          ) : (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                No compliance attestation on record. Please complete the attestation to ensure full compliance.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Recommendations */}
      {dashboardData?.recommendations && dashboardData.recommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recommendations</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {dashboardData.recommendations.map((recommendation: string, idx: number) => (
                <li key={idx} className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">{recommendation}</p>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      
      </>
      )}
    </div>
  );
};

